import { useUserContext } from '../contexts/UserContext';
import { useAuthOptions } from '../hooks/useAuthOptions';
import * as brazeService from '../services/brazeService';

type WrappedFunctions = typeof brazeService;

/**
 * This hook will expose any functions exported from `brazeService`, which will only be invoked if the user has consented to braze
 * @returns Braze functions
 */
const useBraze = () => {
  const {
    consentOptions: { allowBraze },
  } = useUserContext();
  const { isAuthenticated } = useAuthOptions();

  const enableBraze = isAuthenticated && allowBraze;

  const createConditionalBrazeMethod =
    <T extends (...args: any[]) => any>(callback: T) =>
    (...args: Parameters<T>) => {
      if (enableBraze) callback(...args);
    };

  const functions = Object.fromEntries(
    Object.entries(brazeService).map(([key, method]) => {
      return [key, createConditionalBrazeMethod(method)];
    }),
    /**
     * We assert with `as` because Object.entries loses specific key types with Typescript
     */
  ) as WrappedFunctions;

  return functions;
};

export { useBraze };
